import { useEffect } from 'react'
import { useQuery, gql, NetworkStatus } from '@apollo/client'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'
import { NavLink, useLocation } from 'react-router-dom'
import { isEmpty } from 'lodash'

import { Button } from 'components/v2/Button'
import EmptyState from 'components/v2/EmptyState'
import { Loader } from '@10xLabs/react-web/components/Loader'
import { SimpleContent, TabsActions, ContentBody } from 'components/v2/Content'
import { ForbiddenView } from 'features/App/components/ForbiddenView'
import { withLastUrl } from 'features/App/components/WithLastUrl'
import { FEATURES } from 'features/Roles/components/Permissions/constants'
import { SnacksHeader } from 'features/Snacks/components/Header'
import { SnacksTabs } from 'features/Snacks/components/Tabs'
import { GET_COMBO_ITEMS_AUTOCOMPLETE } from 'resolvers/items'
import { filterEmptyValue } from 'utils/object'
import { getQueries } from 'utils/route'
import { canRead, canWrite } from 'features/Users/utils'
import { CombosFilters } from './Filters'
import { CombosList } from './List'
import { CombosPagination } from './Pagination'
import { useLogin } from 'features/Login/hooks'

const LIMIT = 30
const messages = defineMessages({
  helmet: {
    id: 'combos.helmet',
    defaultMessage: 'Combos | Nexbus Admin'
  }
})

const GET_COMBOS = gql`
  query GetComboItems($limit: Int!, $page: Int!, $category: String, $subcategory: String, $comboItemId: ID) {
    comboItems(
      pagination: { size: $limit, page: $page }
      input: { category: $category, subcategory: $subcategory, id: $comboItemId }
    ) {
      total
      items {
        id
        photoPath
        name
        category
        subcategory
        sellingPrice
        minSellingPrice
        isActive
        components {
          quantity
          eligibleItems {
            id
            name
            netContent
            units
            container
          }
        }
      }
    }
  }
`
export const sanitizeComboItem = (comboItemId: TSFixMe, companyId: TSFixMe): TSFixMe => {
  let comboItem
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { data: comboItemsData } = useQuery(GET_COMBO_ITEMS_AUTOCOMPLETE, {
    skip: !comboItemId || !companyId,
    variables: {
      companyId
    }
  })
  if (comboItemsData && comboItemsData.comboItemsAutocomplete) {
    comboItem = comboItemsData.comboItemsAutocomplete.find(({ id }: TSFixMe): TSFixMe => comboItemId === id)
  }
  return comboItem
}
const CombosView = (): TSFixMe => {
  let content
  const location = useLocation()
  const intl = useIntl()
  const login = useLogin()

  useEffect((): TSFixMe => {
    document.title = intl.formatMessage(messages.helmet)
  }, [])

  if (!canRead(FEATURES.ITEM, login.accessToken.policies)) {
    return <ForbiddenView feature={FEATURES.ITEM} />
  }
  // eslint-disable-next-line prefer-const
  let { page = 1, comboItemId, category, subcategory } = getQueries(location.search)
  // @ts-expect-error TS(2559) FIXME Remove this comment to see the full error message
  page = parseInt(page)
  const sanitizedComboItem = sanitizeComboItem(comboItemId, login.accessToken.companyId)
  const queryFilters = filterEmptyValue({
    comboItemId,
    category,
    subcategory
  })
  const {
    loading,
    error,
    data = {},
    refetch,
    networkStatus
    // eslint-disable-next-line react-hooks/rules-of-hooks
  } = useQuery(GET_COMBOS, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    variables: {
      limit: LIMIT,
      page: page - 1,
      ...queryFilters
    }
  })
  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect((): TSFixMe => {
    refetch()
  }, [])
  const filters = {
    category,
    subcategory,
    comboItem: sanitizedComboItem
  }
  if (networkStatus === NetworkStatus.refetch || loading) {
    content = (
      <EmptyState.Container>
        <Loader />
      </EmptyState.Container>
    )
  } else if (error) {
    content = (
      <EmptyState.Container>
        <EmptyState.Description>
          <FormattedMessage id="spanish.combos.error.description" defaultMessage="Hubo un error al cargar sus combos" />
        </EmptyState.Description>
        <EmptyState.Action>
          <Button onClick={(): TSFixMe => refetch()} kind="primary">
            <FormattedMessage id="spanish.combos.error.action" defaultMessage="Recargar sus combos" />
          </Button>
        </EmptyState.Action>
      </EmptyState.Container>
    )
  } else if (isEmpty(data.comboItems.items) && page === 1 && isEmpty(queryFilters)) {
    content = (
      <EmptyState.Container>
        <EmptyState.Description>
          <FormattedMessage id="spanish.combos.empty.description" defaultMessage="No tienes combos todavía" />
        </EmptyState.Description>
        <EmptyState.Action>
          <Button as={NavLink} to="/snacks/combos/new" kind="primary">
            <FormattedMessage id="spanish.combos.empty.action" defaultMessage="Agregar combo" />
          </Button>
        </EmptyState.Action>
      </EmptyState.Container>
    )
  } else if (isEmpty(data.comboItems.items)) {
    content = (
      <EmptyState.Container>
        <EmptyState.Description>
          <FormattedMessage id="spanish.combos.empty.filters.description" defaultMessage="No se encontró combos" />
        </EmptyState.Description>
        <EmptyState.Action>
          <Button as={NavLink} to="/snacks/combos" kind="primary">
            <FormattedMessage id="spanish.combos.empty.filters.action" defaultMessage="Borrar filtros" />
          </Button>
        </EmptyState.Action>
      </EmptyState.Container>
    )
  } else {
    content = (
      <>
        {/* @ts-expect-error TS(2559) FIXME Remove this comment to see the full error message*/}
        <CombosList combos={data.comboItems.items} refetch={refetch} />
        <CombosPagination
          page={page}
          limit={LIMIT}
          totalItems={data.comboItems.total}
          currentItemsCount={data.comboItems.items.length}
        />
      </>
    )
  }
  return (
    <>
      <SnacksHeader />
      <SnacksTabs />
      <SimpleContent>
        {canWrite(FEATURES.ITEM, login.accessToken.policies) && (
          <TabsActions>
            <Button as={NavLink} to="/snacks/combos/new" kind="primary">
              <FormattedMessage id="spanish.products.header.new" defaultMessage="+ Agregar combo" />
            </Button>
          </TabsActions>
        )}
        <ContentBody>
          <CombosFilters filters={filters} companyId={login.accessToken.companyId} />
          <section>{content}</section>
        </ContentBody>
      </SimpleContent>
    </>
  )
}

export default withLastUrl(CombosView)
